import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import clsx from "clsx";

// MUI
import { List, ListItemText, ListItemButton, Paper, Skeleton, Typography, Grid } from "@mui/material";

const fixedListItemClasses = "scroll-side-bar-item !py-[1.5px] !rounded-full";
function AddSections(props) {
	const { component = null, title = "Add", sx = null, isWithDefaultClass = false, isClearComponent = false } = props;

	if (component && isClearComponent) {
		return <>{component}</>;
	}

	return (
		<ListItemButton
			key={"add"}
			disableRipple
			className={clsx({
				[`${fixedListItemClasses} !px-[20px]`]: isWithDefaultClass,
			})}
			sx={sx}
		>
			{component ? (
				<>{component}</>
			) : (
				<ListItemText primary={title || "Add"} className={"scroll-label capitalize font-sans text-[14.5px] shadow-none"} />
			)}
		</ListItemButton>
	);
}
AddSections.propTypes = {
	component: PropTypes.node,
	title: PropTypes.string,
	sx: PropTypes.object,
	isWithDefaultClass: PropTypes.bool,
	isClearComponent: PropTypes.bool,
};

const getPxPadding = (classes = "") => (classes?.includes("system-settings-list-item") ? "!px-[10px]" : "!px-[20px]");

export default function ScrollSideBar(props) {
	const { selectBy = "_id", showBy = "label" } = props;
	const [menus, setMenus] = useState([]);
	const [active, setActive] = useState(null);

	const pxPadding = getPxPadding(props.listItemClasses);

	const handleClick = (item, key) => {
		setActive(item?.[selectBy]);
		if (props.onActionClick) {
			props.onActionClick(item, key);
		}
	};

	useEffect(() => {
		const isItemSelected = Boolean(props.selected);
		if (props.menus) {
			const { menus: listMenus } = props;

			if (props.defaultSelection && !isItemSelected) {
				const defaultSelectBy = props.selectBy || "_id";
				setActive(listMenus[0]?.[defaultSelectBy]);
				if (props.onActionClick) {
					props.onActionClick(listMenus[0], 0);
				}
			}
			setMenus(listMenus);
		}

		if (isItemSelected) {
			setActive(props.selected);
		}

		return () => {
			setMenus([]);
		};
	}, [props]);

	return (
		<Paper
			elevation={0}
			className={"scroll-side-bar-container border-r-[1px] border-table-border"}
			style={{
				backgroundColor: "transparent",
				position: props.position || "relative",
				top: props.top || 0,
				borderRadius: 0,
			}}
			sx={props.sx}
		>
			{props.title && (
				<Grid container mx={"20px"}>
					{props.loading ? (
						<Skeleton width={120} height={28} />
					) : (
						<Typography className="regular-montserrat-label" sx={props.titleSx || null}>
							{props.title}
						</Typography>
					)}
				</Grid>
			)}
			{props.loading ? (
				<List dense className={"scroll-side-bar w-full !pr-[20px]"}>
					{Array.from(Array(3)).map((_, key) => {
						const itemKey = `loading_scroll_${showBy}_${key}`;
						return (
							<Skeleton
								key={itemKey}
								variant="rectangular"
								width={"100%"}
								height={30}
								sx={{ marginBottom: "5px", borderRadius: "25px" }}
							/>
						);
					})}
				</List>
			) : (
				<List dense className={"scroll-side-bar w-full !pr-[20px] !min-h-72"} sx={{ maxHeight: "615px", overflow: "auto" }}>
					{menus.map((item, key) => {
						if (props.deleting && props.deleting === item?.[selectBy]) {
							return (
								<Skeleton
									key={"new"}
									variant="rectangular"
									width={"100%"}
									height={30}
									sx={{ marginBottom: "5px", borderRadius: "25px" }}
								/>
							);
						} else {
							const isSelected = item?.[selectBy] === active;
							const itemKey = `scroll_list_item_${showBy}_${key}`;
							return (
								<ListItemButton
									key={itemKey}
									id={props.scrollBy ? `scrollable-list-item-${item[props.scrollBy]}` : key}
									className={clsx({
										[`${fixedListItemClasses} ${pxPadding} active:!bg-primary`]: true,
										"!bg-primary": isSelected,
										"hover:!bg-primary-opacity-20": !isSelected,
										[props.listItemClasses]: props.listItemClasses,
									})}
									selected={isSelected}
									onClick={() => handleClick(item, key)}
								>
									<ListItemText
										primary={
											<Typography
												className={clsx({
													"regular-pt-sans-pro-narrow-label hover:opacity-1": true,
													"!text-white": isSelected,
												})}
												noWrap
												sx={{ paddingLeft: 2 }}
											>
												{item?.[showBy] || ""}
											</Typography>
										}
										className={"scroll-label capitalize font-sans text-[14.5px] shadow-none"}
									/>
								</ListItemButton>
							);
						}
					})}

					{props.adding && (
						<Skeleton
							key={"new"}
							variant="rectangular"
							width={"100%"}
							height={30}
							sx={{ marginBottom: "5px", borderRadius: "25px" }}
						/>
					)}

					{props.addProperties && <AddSections {...props.addProperties} />}
				</List>
			)}
		</Paper>
	);
}
ScrollSideBar.propTypes = {
	menus: PropTypes.array,
	selected: PropTypes.string,
	selectBy: PropTypes.string,
	showBy: PropTypes.string,
	orderBy: PropTypes.string,
	loading: PropTypes.bool,
	deleting: PropTypes.string,
	adding: PropTypes.bool,
	scrollBy: PropTypes.string,
	title: PropTypes.string,
	position: PropTypes.string,
	top: PropTypes.string,
	listItemClasses: PropTypes.string,
	addProperties: PropTypes.object,
	sx: PropTypes.object,
	titleSx: PropTypes.object,
	addingProperties: PropTypes.object,
	defaultSelection: PropTypes.bool,
	onActionClick: PropTypes.func,
};
